var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { PreviewTableView } from "@components/PreviewTable/PreviewTable";
import { DataSourceType } from "@types";
import { QueryBuilderContext } from "@query-builder/QueryBuilderContext";
import { InitialSourceId, appendSchema, findNextItemId, findNextStepId, getQueryBySelectedStepId, getQueryForSelectedSource, getRegisteredStepByType, getSchemaForSource, getSchemaForStep, validateParam, validateQuery, validateStep } from "@utils";
import { checkStatus, executeQuery, exportCsv } from "@query-engine-service";
import { StepConfigurator } from "../StepConfigurator/StepConfigurator";
import { StepSelector } from "@components/StepConfigurator/StepSelector/StepSelector";
import { applyFormatters, ButtonComponent, ButtonGroup, Icon, IconButton, hasValue, Input, Select, SimpleConfigurationTable, useAlert, useToast } from "uxp/components";
import { Loading } from "@components/Loading/Loading";
import { Copilot } from "@components/Copilot/Copilot";
import { renderIcon } from "@components/DataSourceBrowser/DataSourceBrowser";
import { SortableList } from "@components/SortableList/SortableList";
import { PieChartPreview } from "./visualizers/PieChartPreview";
import { BarChartPreview } from "./visualizers/BarChartPreview";
import { isSourceConfigurable, ParamsEditor } from "./ParamEditor";
import { FormSection } from "@components/FormSection/FormSection";
import { DataSourceBrowserModal } from "@components/DataSourceBrowser/DataSourceBrowserModal";
import cloneDeep from 'lodash-es/cloneDeep.js';
import '@components/StepConfigurator/Steps/steps';
import './QueryEditor.scss';
export const QueryEditor = props => {
    const { query, onChange } = props;
    const { sources, steps, sourceOnly } = query;
    const context = useContext(QueryBuilderContext);
    const alerts = useAlert();
    const toast = useToast();
    const $L = (key) => context.uxpContext.$L(key);
    const [selectedStep, setSelectedStep] = useState(null);
    const [selectedStepData, setSelectedStepData] = useState(null);
    const [selectedStepDataError, setSelectedStepDataError] = useState(null);
    const [loadingStepData, setLoadingStepData] = useState(true);
    const [previewMode, setPreviewMode] = useState('table');
    const [contentMode, setContentMode] = useState('config');
    const [supportParams, setSupportParams] = useState(false);
    const [showSourceBrowser, setShowSourceBrowser] = useState(false);
    const [previewHeight, setPreviewHeight] = useState(280);
    const [showPreview, setShowPreview] = useState(true);
    const containerRef = useRef(null);
    const previewUpdateTimerRef = useRef(null);
    const schemaUpdateTimerRef = useRef(null);
    const hasChanged = useRef(false);
    const nextStepIdRef = useRef(null);
    useEffect(() => {
        // initial schema update if a query is passed
        if (query) {
            updateSchema(query);
        }
    }, []);
    useEffect(() => {
        if (!!query && (!hasValue(selectedStep) || hasValue(nextStepIdRef.current))) {
            if (hasValue(nextStepIdRef.current)) {
                setSelectedStep(nextStepIdRef.current);
                nextStepIdRef.current = null;
            }
            else {
                const _steps = (query === null || query === void 0 ? void 0 : query.steps) || [];
                setSelectedStep(_steps.length > 0 ? _steps[_steps.length - 1].id : InitialSourceId);
            }
        }
    }, [query]);
    useEffect(() => {
        if (!!selectedStep) {
            hasChanged.current = true;
            if (previewUpdateTimerRef.current)
                clearTimeout(previewUpdateTimerRef.current);
            previewUpdateTimerRef.current = setTimeout(getStepData, 1000);
        }
    }, [selectedStep, query]);
    useEffect(() => {
        console.log('query_________', query);
    }, [query]);
    useEffect(() => {
        checkStatus(context).then(res => {
            var _a;
            setSupportParams(((_a = res === null || res === void 0 ? void 0 : res.details) === null || _a === void 0 ? void 0 : _a.supportParams) || false);
        });
    }, []);
    function updateSchema(_query) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            if (!selectedStep || selectedStep == InitialSourceId)
                return;
            // update schema for the step 
            const schema = yield getSchemaForStep(context, _query, selectedStep);
            let updatedQuery = appendSchema(_query, schema, selectedStep);
            // validate steps after the current step 
            // generate schem for steps after the current step 
            const selectedStepIndex = (_a = updatedQuery === null || updatedQuery === void 0 ? void 0 : updatedQuery.steps) === null || _a === void 0 ? void 0 : _a.findIndex(s => s.id == selectedStep);
            if (selectedStepIndex >= 0 && selectedStepIndex < ((_b = updatedQuery === null || updatedQuery === void 0 ? void 0 : updatedQuery.steps) === null || _b === void 0 ? void 0 : _b.length) - 1) {
                const stepsAfterCurrentStep = (_c = updatedQuery === null || updatedQuery === void 0 ? void 0 : updatedQuery.steps) === null || _c === void 0 ? void 0 : _c.slice(selectedStepIndex);
                for (const s of stepsAfterCurrentStep) {
                    const schema = yield getSchemaForStep(context, updatedQuery, s.id);
                    updatedQuery = appendSchema(updatedQuery, schema, s.id);
                }
            }
            onChange(updatedQuery);
        });
    }
    function beforeChangeQuery(_query) {
        return __awaiter(this, void 0, void 0, function* () {
            // wait and update schema for the step 
            if (schemaUpdateTimerRef.current)
                clearTimeout(schemaUpdateTimerRef.current);
            schemaUpdateTimerRef.current = setTimeout(() => {
                updateSchema(_query);
            }, 500);
            // commit the changes to reflect immidiately in the ui
            onChange(_query);
        });
    }
    function removeStep(step) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const isConfirmed = yield alerts.confirm($L('lqe.qe.confirm-remove-step'));
                if (isConfirmed) {
                    const filteredSteps = query.steps.filter(s => s.id !== step.id);
                    // if the step is selected 
                    if (selectedStep == step.id) {
                        const nextStepId = findNextStepId(query, selectedStep);
                        nextStepIdRef.current = nextStepId;
                    }
                    // commit the changes 
                    beforeChangeQuery(Object.assign(Object.assign({}, query), { steps: filteredSteps }));
                }
            }
            catch (e) { }
        });
    }
    function removeSource(source) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const isConfirmed = yield alerts.confirm($L('lqe.qe.confirm-remove-source'));
                if (isConfirmed) {
                    const _sources = [...((query === null || query === void 0 ? void 0 : query.sources) || [])];
                    const index = _sources.findIndex(s => s.id == source.id);
                    if (index < 0)
                        return;
                    _sources.splice(index, 1);
                    // if the step is selected 
                    if (selectedStep == source.id) {
                        const nextSourceId = findNextItemId(query.sources, index);
                        nextStepIdRef.current = nextSourceId;
                    }
                    // commit the changes 
                    beforeChangeQuery(Object.assign(Object.assign({}, query), { sources: _sources }));
                }
            }
            catch (e) { }
        });
    }
    function removeParam(param) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const isConfirmed = yield alerts.confirm($L('lqe.qe.confirm-remove-param'));
                if (isConfirmed) {
                    const _params = [...((query === null || query === void 0 ? void 0 : query.params) || [])];
                    const index = _params === null || _params === void 0 ? void 0 : _params.findIndex(s => s.id == param.id);
                    if (index < 0)
                        return;
                    _params.splice(index, 1);
                    if (selectedStep == param.id) {
                        const nextParamId = findNextItemId(query.params || [], index);
                        nextStepIdRef.current = nextParamId;
                    }
                    // commit the changes 
                    beforeChangeQuery(Object.assign(Object.assign({}, query), { params: _params }));
                }
            }
            catch (e) { }
        });
    }
    function changeSource(newSource) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // get source schema
            const schema = yield getSchemaForSource(context, {
                sources: [newSource],
                steps: []
            }, newSource.id);
            newSource.schema = schema;
            // Update the first source in the query
            const updatedQuery = cloneDeep(query);
            updatedQuery.sources[0] = newSource;
            // Ask user what to do with existing steps
            if (updatedQuery.steps.length > 0 && ((_b = (_a = query === null || query === void 0 ? void 0 : query.sources) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.id) !== (newSource === null || newSource === void 0 ? void 0 : newSource.id)) {
                const choice = yield new Promise(resolve => {
                    alerts.confirm({
                        title: $L('lqe.qe.change-source-title'),
                        content: $L('lqe.qe.change-source-content'),
                        confirmButtonTitle: $L('lqe.qe.change-source-remove'),
                        cancelButtonTitle: $L('lqe.qe.change-source-keep')
                    }).then(result => {
                        if (result === true)
                            resolve('remove');
                        else if (result === false)
                            resolve('keep');
                    }).catch(() => resolve('keep'));
                });
                if (choice === 'remove') {
                    updatedQuery.steps = [];
                    setSelectedStep(InitialSourceId);
                }
            }
            onChange(updatedQuery);
            setShowSourceBrowser(false);
        });
    }
    function getStepData() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const _query = contentMode == 'datasources' ? getQueryForSelectedSource(query, selectedStep) : getQueryBySelectedStepId(query, selectedStep);
            const { isValid } = validateQuery(_query);
            if (!isValid) {
                setSelectedStepDataError($L('lqe.qe.query-invalid'));
                return;
            }
            setLoadingStepData(true);
            hasChanged.current = false;
            /**
             * Configure default parameter values for pipeline preview.
             *
             * When setting up a data pipeline, users define parameters with default values.
             * During configuration/preview mode, we need to substitute these parameters
             * with their default values so users can see sample data output.
             *
            */
            if (((_a = query === null || query === void 0 ? void 0 : query.params) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                _query.configuredParams = query.params.reduce((final, p) => {
                    final[p.name] = hasValue(p.defaultValue) ? applyFormatters(p.defaultValue, [(p === null || p === void 0 ? void 0 : p.type) || 'string']) : null;
                    return final;
                }, {});
            }
            const { data, error } = yield executeQuery(context, _query, {
                mode: 'preview'
            });
            setSelectedStepDataError(error || null);
            setSelectedStepData((data === null || data === void 0 ? void 0 : data.result) || []);
            setLoadingStepData(false);
        });
    }
    function handleStepSelector(type) {
        var _a, _b, _c;
        let prevSource = '';
        const prevStep = (_a = query === null || query === void 0 ? void 0 : query.steps) === null || _a === void 0 ? void 0 : _a[((_b = query === null || query === void 0 ? void 0 : query.steps) === null || _b === void 0 ? void 0 : _b.length) - 1];
        if (prevStep) {
            prevSource = prevStep.id;
        }
        if (!prevStep) {
            const firstSource = ((_c = query === null || query === void 0 ? void 0 : query.sources) === null || _c === void 0 ? void 0 : _c[0]) || null;
            if (!!firstSource) {
                prevSource = firstSource.id;
            }
        }
        const id = `${type}-${Date.now()}`;
        const name = type === null || type === void 0 ? void 0 : type.toUpperCase();
        const registeredStep = getRegisteredStepByType(type);
        if (!registeredStep)
            return toast.error($L('lqe.qe.invalid-step'));
        const newStep = registeredStep.onSelectStep({
            id,
            name,
            schema: [],
            type
        }, prevSource);
        const _query = Object.assign({}, query);
        _query.steps.push(newStep);
        onChange(_query);
        setSelectedStep(id);
    }
    ;
    function renderInitialSourceMessage() {
        var _a;
        if (selectedStep !== InitialSourceId)
            return null;
        const source = ((_a = query === null || query === void 0 ? void 0 : query.sources) === null || _a === void 0 ? void 0 : _a[0]) || null;
        if (!source)
            return null;
        function updateInitialSource(updatedSource) {
            const _query = Object.assign({}, query);
            _query.sources[0] = updatedSource;
            onChange(Object.assign({}, _query));
        }
        const { isConfigurable } = isSourceConfigurable(source);
        return React.createElement("div", { className: "lqe-sc-7c26fa76" },
            React.createElement("div", { className: "lqe-sc__desc-7c26fa76" }, $L('lqe.qe.initial-source-desc')),
            React.createElement(FormSection, { label: $L('lqe.common.name') },
                React.createElement("div", { className: "lqe-sc__ro-val-7c26fa76" }, source.name),
                React.createElement(ButtonComponent, { title: $L('lqe.qe.change-source-btn'), leftIcon: "fas repeat", onClick: () => setShowSourceBrowser(true), variant: "secondary", size: "small" })),
            hasValue(source === null || source === void 0 ? void 0 : source.description) && React.createElement(FormSection, { label: $L('lqe.common.description') },
                React.createElement("div", { className: "lqe-sc__ro-val-7c26fa76" }, source.description)),
            React.createElement(FormSection, { label: $L('lqe.common.type') },
                React.createElement("div", { className: "lqe-sc__ro-val-7c26fa76", style: {
                        textTransform: 'uppercase'
                    } }, source.type)),
            source.type == DataSourceType.COLLECTION && React.createElement(FormSection, { label: $L('lqe.common.model') },
                React.createElement("div", { className: "lqe-sc__ro-val-7c26fa76" }, source === null || source === void 0 ? void 0 : source.model)),
            isConfigurable && React.createElement(FormSection, { label: $L('lqe.qe.configure-params') },
                React.createElement(ParamsEditor, { source: source, onChange: updateInitialSource, query: query })),
            !sourceOnly && React.createElement("div", { className: "lqe-qe__info-7c26fa76" },
                React.createElement("div", { className: "lqe-qe__info-msg-7c26fa76" }, $L('lqe.qe.initial-source-msg1')),
                React.createElement("div", { className: "lqe-qe__info-msg-7c26fa76" }, $L('lqe.qe.initial-source-msg2'))));
    }
    function renderConfiguration() {
        if (!selectedStep || selectedStep == InitialSourceId)
            return null;
        return React.createElement(StepConfigurator, { query: query, onChange: beforeChangeQuery, selectedStepId: selectedStep, context: context });
    }
    function renderSourceDetails() {
        const source = ((query === null || query === void 0 ? void 0 : query.sources) || []).find(s => s.id == selectedStep);
        if (!source)
            return null;
        function updateSource(updatedSource) {
            const _query = Object.assign({}, query);
            const index = _query.sources.findIndex(s => s.id == selectedStep);
            if (index < 0)
                return;
            _query.sources.splice(index, 1, updatedSource);
            onChange(Object.assign({}, _query));
        }
        const { isConfigurable } = isSourceConfigurable(source);
        return React.createElement("div", { className: "lqe-sc-7c26fa76" },
            React.createElement("div", { className: "lqe-sc__desc-7c26fa76" }, $L('lqe.qe.source-desc')),
            React.createElement(FormSection, { label: $L('lqe.common.name') },
                React.createElement("div", { className: "lqe-sc__ro-val-7c26fa76" }, source.name)),
            hasValue(source === null || source === void 0 ? void 0 : source.description) && React.createElement(FormSection, { label: $L('lqe.common.description') },
                React.createElement("div", { className: "lqe-sc__ro-val-7c26fa76" }, source.description)),
            React.createElement(FormSection, { label: $L('lqe.common.type') },
                React.createElement("div", { className: "lqe-sc__ro-val-7c26fa76", style: {
                        textTransform: 'uppercase'
                    } }, source.type)),
            source.type == DataSourceType.COLLECTION && React.createElement(FormSection, { label: $L('lqe.common.model') },
                React.createElement("div", { className: "lqe-sc__ro-val-7c26fa76" }, source === null || source === void 0 ? void 0 : source.model)),
            isConfigurable && React.createElement(FormSection, { label: $L('lqe.qe.configure-params') },
                React.createElement(ParamsEditor, { source: source, onChange: updateSource, query: query })));
    }
    function renderParamDetails() {
        const param = ((query === null || query === void 0 ? void 0 : query.params) || []).find(s => s.id == selectedStep);
        if (!param)
            return null;
        const { errors } = validateParam(query, param);
        function updateParamDetails(field, value) {
            const _query = Object.assign({}, query);
            const index = (_query === null || _query === void 0 ? void 0 : _query.params).findIndex(s => s.id == selectedStep);
            if (index < 0)
                return;
            param[field] = value;
            _query.params.splice(index, 1, param);
            onChange(Object.assign({}, _query));
        }
        return React.createElement("div", { className: "lqe-sc-7c26fa76" },
            React.createElement("div", { className: "lqe-qe__src-info-7c26fa76" },
                React.createElement(FormSection, { label: $L('lqe.common.name'), error: errors === null || errors === void 0 ? void 0 : errors.name },
                    React.createElement(Input, { value: (param === null || param === void 0 ? void 0 : param.name) || '', onChange: v => {
                            updateParamDetails('name', v);
                        } })),
                React.createElement(FormSection, { label: $L('lqe.common.type'), error: errors === null || errors === void 0 ? void 0 : errors.type },
                    React.createElement(Select, { selected: (param === null || param === void 0 ? void 0 : param.type) || '', onChange: v => {
                            updateParamDetails('type', v);
                        }, options: [{
                                label: $L('lqe.common.type-text'),
                                value: 'string'
                            }, {
                                label: $L('lqe.common.type-number'),
                                value: 'number'
                            }, {
                                label: $L('lqe.common.type-boolean'),
                                value: 'boolean'
                            }] })),
                React.createElement(FormSection, { label: $L('lqe.common.default-value'), error: errors === null || errors === void 0 ? void 0 : errors.defaultValue },
                    React.createElement("small", null,
                        React.createElement("i", null, $L('lqe.qe.default-value-hint'))),
                    React.createElement(Input, { value: (param === null || param === void 0 ? void 0 : param.defaultValue) || '', onChange: v => {
                            updateParamDetails('defaultValue', v);
                        } }))));
    }
    function handleEdit() {
        var _a, _b, _c;
        const queryId = ((_c = (((_b = (_a = query === null || query === void 0 ? void 0 : query.sources) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.id) || '').split('/')) === null || _c === void 0 ? void 0 : _c[1]) || null;
        if (!queryId)
            return;
        const popup = window.open(`/apps/lucy/canvas/queries?edit=${queryId}`, '_blank', 'width=800,height=600');
        const interval = setInterval(() => {
            if (!popup || popup.closed) {
                clearInterval(interval);
                props.onUpdateSavedQuery(queryId);
            }
        }, 500);
    }
    function renderInitialSource() {
        const initialSource = (sources === null || sources === void 0 ? void 0 : sources[0]) || null;
        return React.createElement("div", { className: "lqe-qe__pl-item-7c26fa76" },
            React.createElement("div", { className: classNames("lqe-qe__pl-step-7c26fa76", {
                    "lqe-qe__pl-step--invalid-7c26fa76": !initialSource,
                    "lqe-qe__pl-step--selected-7c26fa76": contentMode === 'config' && selectedStep == InitialSourceId
                }), onClick: () => {
                    setSelectedStep(InitialSourceId);
                    setContentMode('config');
                } },
                React.createElement("div", { className: "lqe-qe__pl-num-7c26fa76" }, "1"),
                React.createElement("div", { className: "lqe-qe__pl-lbl-7c26fa76" }, initialSource ? initialSource.name : $L('lqe.qe.no-source')),
                !initialSource && React.createElement("div", { className: "lqe-qe__pl-err-7c26fa76" },
                    React.createElement(Icon, { icon: "fas exclamation-triangle" }))),
            !sourceOnly && React.createElement("div", { className: "lqe-qe__pl-conn-7c26fa76" }),
            sourceOnly && React.createElement("div", { className: "lqe-qe__step-edit-7c26fa76" },
                React.createElement("div", { className: "lqe-qe__step-edit-msg-7c26fa76" }, $L('lqe.qe.saved-query-msg')),
                React.createElement(ButtonComponent, { leftIcon: "fas pencil", title: $L('lqe.qe.edit-query'), onClick: handleEdit, variant: "secondary", size: "small" })));
    }
    function renderStep(s, i) {
        const active = contentMode === 'config' && selectedStep == s.id;
        const { isValid } = validateStep(query, s);
        const registeredStep = getRegisteredStepByType(s.type);
        return React.createElement("div", { className: "lqe-qe__pl-item-7c26fa76" },
            React.createElement("div", { className: classNames("lqe-qe__pl-step-7c26fa76", {
                    "lqe-qe__pl-step--selected-7c26fa76": active,
                    "lqe-qe__pl-step--invalid-7c26fa76": !isValid
                }), onClick: () => {
                    setSelectedStep(s.id);
                    setContentMode('config');
                } },
                React.createElement("div", { className: "lqe-qe__pl-num-7c26fa76" }, i + 2),
                (registeredStep === null || registeredStep === void 0 ? void 0 : registeredStep.icon) && React.createElement("div", { className: "lqe-qe__pl-step-ico-7c26fa76", style: {
                        maskImage: `url(${registeredStep.icon})`,
                        WebkitMaskImage: `url(${registeredStep.icon})`
                    } }),
                React.createElement("div", { className: "lqe-qe__pl-lbl-7c26fa76" }, s.name),
                !isValid && React.createElement("div", { className: "lqe-qe__pl-err-7c26fa76" },
                    React.createElement(Icon, { icon: "fas exclamation-triangle" })),
                React.createElement("div", { className: "lqe-qe__pl-rm-wrap-7c26fa76" },
                    React.createElement("div", { className: "lqe-qe__pl-rm-7c26fa76", onClick: e => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeStep(s);
                        } },
                        React.createElement(Icon, { icon: ['fas', 'times'] })))),
            React.createElement("div", { className: "lqe-qe__pl-conn-7c26fa76" }));
    }
    function downloadCsv() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield exportCsv(context, query);
            }
            catch (e) {
                alert($L('lqe.qe.export-error'));
            }
        });
    }
    function handlePreviewDragStart(e) {
        var _a;
        e.preventDefault();
        const startY = e.clientY;
        const startH = previewHeight;
        const containerH = ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight) || 600;
        const maxH = Math.floor(containerH * 0.75);
        const onMove = (ev) => {
            const delta = startY - ev.clientY;
            setPreviewHeight(Math.min(Math.max(startH + delta, 80), maxH));
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }
    function renderPreview() {
        if (loadingStepData)
            return React.createElement(Loading, null);
        if (hasChanged.current)
            return React.createElement(Loading, { message: $L('lqe.qe.preview-loading') });
        if (!!selectedStepDataError) {
            return React.createElement("div", { className: "lqe-qe__preview-inner-7c26fa76" },
                React.createElement("div", { className: "lqe-qe__preview-error-7c26fa76" },
                    React.createElement(Icon, { icon: ['fas', 'exclamation-triangle'], className: "lqe-qe__preview-error-ico-7c26fa76" }),
                    React.createElement("div", { className: "lqe-qe__preview-error-title-7c26fa76" }, $L('lqe.qe.preview-error-title')),
                    React.createElement("div", { className: "lqe-qe__preview-error-msg-7c26fa76" }, selectedStepDataError)));
        }
        let schemaProvider = null;
        if (selectedStep == InitialSourceId) {
            schemaProvider = props.query.sources[0];
        }
        else {
            schemaProvider = props.query.steps.find(x => x.id == selectedStep);
        }
        return React.createElement("div", { className: "lqe-qe__preview-inner-7c26fa76 lqe-qe__preview-inner--has-hd-7c26fa76" },
            React.createElement("div", { className: "lqe-qe__preview-hd-7c26fa76" },
                React.createElement("div", { className: "lqe-qe__preview-hd-title-7c26fa76" }, $L('lqe.qe.preview')),
                React.createElement("div", { className: "lqe-qe__preview-hd-actions-7c26fa76" },
                    React.createElement(ButtonComponent, { title: $L('lqe.qe.export'), leftIcon: "fas download", onClick: downloadCsv, variant: "secondary", size: "small" }),
                    React.createElement(ButtonGroup, { activeId: previewMode, variant: "secondary", onButtonClick: id => setPreviewMode(id), buttons: [{
                                id: 'table',
                                leftIcon: 'fas table'
                            }, {
                                id: 'bar-chart',
                                leftIcon: 'fas chart-bar'
                            }, {
                                id: 'pie-chart',
                                leftIcon: 'fas chart-pie'
                            }] }),
                    React.createElement(ButtonComponent, { leftIcon: "fas times", variant: "secondary", size: "small", onClick: () => setShowPreview(false) }))),
            previewMode == 'table' && React.createElement("div", { className: "lqe-qe__preview-body-7c26fa76" },
                React.createElement(PreviewTableView, { data: selectedStepData || [] })),
            previewMode == 'pie-chart' && React.createElement("div", { className: "lqe-qe__preview-body-7c26fa76" },
                React.createElement(PieChartPreview, { schema: schemaProvider === null || schemaProvider === void 0 ? void 0 : schemaProvider.schema, context: context, data: selectedStepData || [] })),
            previewMode == 'bar-chart' && React.createElement("div", { className: "lqe-qe__preview-body-7c26fa76" },
                React.createElement(BarChartPreview, { schema: schemaProvider === null || schemaProvider === void 0 ? void 0 : schemaProvider.schema, context: context, data: selectedStepData || [] })));
    }
    function renderPipeline() {
        return React.createElement(React.Fragment, null,
            React.createElement("div", { className: "lqe-qe__section-body-7c26fa76" },
                renderInitialSource(),
                React.createElement(SortableList, { items: steps, renderItem: (step, i) => renderStep(step, i), onSort: (sorted) => {
                        onChange(Object.assign(Object.assign({}, query), { steps: sorted }));
                    } }),
                !sourceOnly ? React.createElement(StepSelector, { onSelect: handleStepSelector }) : null));
    }
    function renderDataSourcesContent() {
        var _a;
        return React.createElement("div", { className: "lqe-qe__ds-grid-7c26fa76" }, (_a = query === null || query === void 0 ? void 0 : query.sources) === null || _a === void 0 ? void 0 : _a.map((s, i) => {
            var _a;
            return React.createElement("div", { className: "lqe-qe__ds-card-7c26fa76", key: i },
                i > 0 && React.createElement("div", { className: "lqe-qe__ds-card-rm-7c26fa76" },
                    React.createElement(IconButton, { type: "close", variant: "danger", size: "small", onClick: () => removeSource(s) })),
                React.createElement("div", { className: "lqe-qe__ds-card-hd-7c26fa76" },
                    React.createElement("div", { className: "lqe-qe__ds-card-ico-7c26fa76" }, renderIcon(s.type)),
                    React.createElement("div", { className: "lqe-qe__ds-card-name-7c26fa76" }, s.name)),
                React.createElement("div", { className: "lqe-qe__ds-card-body-7c26fa76" },
                    React.createElement("div", { className: "lqe-qe__ds-card-row-7c26fa76" },
                        React.createElement("span", { className: "lqe-qe__ds-card-lbl-7c26fa76" }, $L('lqe.common.type')),
                        React.createElement("span", { className: "lqe-qe__ds-card-val-7c26fa76" }, (_a = s.type) === null || _a === void 0 ? void 0 : _a.toUpperCase())),
                    hasValue(s.model) && React.createElement("div", { className: "lqe-qe__ds-card-row-7c26fa76" },
                        React.createElement("span", { className: "lqe-qe__ds-card-lbl-7c26fa76" }, $L('lqe.common.model')),
                        React.createElement("span", { className: "lqe-qe__ds-card-val-7c26fa76" }, s.model)),
                    hasValue(s.table) && React.createElement("div", { className: "lqe-qe__ds-card-row-7c26fa76" },
                        React.createElement("span", { className: "lqe-qe__ds-card-lbl-7c26fa76" }, $L('lqe.common.table')),
                        React.createElement("span", { className: "lqe-qe__ds-card-val-7c26fa76" }, s.table)),
                    hasValue(s.collection) && React.createElement("div", { className: "lqe-qe__ds-card-row-7c26fa76" },
                        React.createElement("span", { className: "lqe-qe__ds-card-lbl-7c26fa76" }, $L('lqe.common.collection')),
                        React.createElement("span", { className: "lqe-qe__ds-card-val-7c26fa76" }, s.collection))));
        }));
    }
    function renderParamsContent() {
        const typeOptions = [{
                label: $L('lqe.common.type-text'),
                value: 'string'
            }, {
                label: $L('lqe.common.type-number'),
                value: 'number'
            }, {
                label: $L('lqe.common.type-boolean'),
                value: 'boolean'
            }];
        return React.createElement("div", { className: "lqe-qe__params-7c26fa76" },
            React.createElement(SimpleConfigurationTable, { uxpContext: context === null || context === void 0 ? void 0 : context.uxpContext, data: () => __awaiter(this, void 0, void 0, function* () {
                    return ((query === null || query === void 0 ? void 0 : query.params) || []).map(p => (Object.assign({}, p)));
                }), columns: [{
                        id: 'name',
                        label: $L('lqe.common.name'),
                        renderColumn: item => item.name,
                        renderEditItem: (item, update) => React.createElement(Input, { value: item.name || '', onChange: v => update(Object.assign(Object.assign({}, item), { name: v })) }),
                        renderNewItem: (item, update) => React.createElement(Input, { value: item.name || '', onChange: v => update(Object.assign(Object.assign({}, item), { name: v })) })
                    }, {
                        id: 'type',
                        label: $L('lqe.common.type'),
                        renderColumn: item => { var _a; return ((_a = typeOptions.find(o => o.value === item.type)) === null || _a === void 0 ? void 0 : _a.label) || item.type; },
                        renderEditItem: (item, update) => React.createElement(Select, { selected: item.type || 'string', onChange: v => update(Object.assign(Object.assign({}, item), { type: v })), options: typeOptions }),
                        renderNewItem: (item, update) => React.createElement(Select, { selected: item.type || 'string', onChange: v => update(Object.assign(Object.assign({}, item), { type: v })), options: typeOptions })
                    }, {
                        id: 'defaultValue',
                        label: $L('lqe.common.default-value'),
                        renderColumn: item => item.defaultValue,
                        renderEditItem: (item, update) => React.createElement(Input, { value: item.defaultValue || '', onChange: v => update(Object.assign(Object.assign({}, item), { defaultValue: v })) }),
                        renderNewItem: (item, update) => React.createElement(Input, { value: item.defaultValue || '', onChange: v => update(Object.assign(Object.assign({}, item), { defaultValue: v })) })
                    }], onAddItem: (item) => __awaiter(this, void 0, void 0, function* () {
                    const id = 'p-' + Date.now();
                    const params = [...((query === null || query === void 0 ? void 0 : query.params) || []), {
                            id,
                            name: item.name || '',
                            type: item.type || 'string',
                            defaultValue: item.defaultValue || ''
                        }];
                    onChange(Object.assign(Object.assign({}, query), { params }));
                    return {
                        success: true
                    };
                }), onUpdateItem: (item) => __awaiter(this, void 0, void 0, function* () {
                    const params = ((query === null || query === void 0 ? void 0 : query.params) || []).map(p => p.id === item.id ? Object.assign(Object.assign({}, p), item) : p);
                    onChange(Object.assign(Object.assign({}, query), { params }));
                    return {
                        success: true
                    };
                }), onDeleteItem: (item) => __awaiter(this, void 0, void 0, function* () {
                    const params = ((query === null || query === void 0 ? void 0 : query.params) || []).filter(p => p.id !== item.id);
                    onChange(Object.assign(Object.assign({}, query), { params }));
                    return {
                        success: true
                    };
                }) }));
    }
    function addNewParam() {
        const params = (query === null || query === void 0 ? void 0 : query.params) || [];
        const id = 'p-' + new Date().getTime();
        params.push({
            id: id,
            name: '',
            type: 'string'
        });
        onChange(Object.assign(Object.assign({}, query), { params: params }));
        nextStepIdRef.current = id;
    }
    function renderParams() {
        var _a, _b;
        return React.createElement(React.Fragment, null,
            React.createElement("div", { className: "lqe-qe__section-title-7c26fa76" }, $L('lqe.qe.parameters')),
            React.createElement("div", { className: "lqe-qe__section-body-7c26fa76" },
                !(query === null || query === void 0 ? void 0 : query.params) || ((_a = query === null || query === void 0 ? void 0 : query.params) === null || _a === void 0 ? void 0 : _a.length) == 0 ? React.createElement("div", { className: "lqe-qe__section-empty-7c26fa76" }, $L('lqe.qe.no-params')) : null, (_b = query === null || query === void 0 ? void 0 : query.params) === null || _b === void 0 ? void 0 :
                _b.map((s, i) => {
                    const { isValid } = validateParam(query, s);
                    return React.createElement("div", { className: classNames("lqe-qe__step-7c26fa76", {
                            "lqe-qe__step--selected-7c26fa76": selectedStep == s.id,
                            "lqe-qe__step--invalid-7c26fa76": !isValid
                        }), onClick: () => setSelectedStep(s.id), key: i },
                        React.createElement("div", { className: "lqe-qe__step-ico-7c26fa76" },
                            React.createElement(Icon, { icon: ['fas', 'sliders-h'] })),
                        React.createElement("div", { className: "lqe-qe__step-lbl-7c26fa76" }, s.name),
                        React.createElement("div", { className: "lqe-qe__step-rm-7c26fa76", onClick: e => {
                                e.preventDefault();
                                e.stopPropagation();
                                removeParam(s);
                            } },
                            React.createElement(Icon, { icon: ['fas', 'times'] })));
                }),
                React.createElement("div", { className: "lqe-ss__btn-wrap-7c26fa76" },
                    React.createElement("div", { className: "lqe-ss__btn-7c26fa76", onClick: addNewParam }, $L('lqe.qe.add-param')))));
    }
    function renderContentHeader() {
        if (contentMode === 'datasources' || contentMode === 'params')
            return null;
        if (selectedStep === InitialSourceId) {
            const source = (sources === null || sources === void 0 ? void 0 : sources[0]) || null;
            return React.createElement(React.Fragment, null,
                React.createElement("span", { className: "lqe-qe__panel-hd-step-7c26fa76" }, "Step 1:"),
                React.createElement("div", { className: "lqe-qe__panel-hd-ico-7c26fa76" }, renderIcon(source === null || source === void 0 ? void 0 : source.type)),
                React.createElement("span", { className: "lqe-qe__panel-hd-name-7c26fa76" }, (source === null || source === void 0 ? void 0 : source.name) || $L('lqe.qe.no-source')));
        }
        const step = ((query === null || query === void 0 ? void 0 : query.steps) || []).find(s => s.id === selectedStep);
        if (step) {
            const stepIndex = ((query === null || query === void 0 ? void 0 : query.steps) || []).findIndex(s => s.id === selectedStep) + 2;
            const registeredStep = getRegisteredStepByType(step.type);
            return React.createElement(React.Fragment, null,
                React.createElement("span", { className: "lqe-qe__panel-hd-step-7c26fa76" },
                    "Step ",
                    stepIndex,
                    ":"),
                React.createElement("div", { className: "lqe-qe__panel-hd-ico-7c26fa76" }, (registeredStep === null || registeredStep === void 0 ? void 0 : registeredStep.icon) && React.createElement("div", { className: "lqe-qe__panel-hd-ico-img-7c26fa76", style: {
                        maskImage: `url(${registeredStep.icon})`,
                        WebkitMaskImage: `url(${registeredStep.icon})`
                    } })),
                React.createElement("span", { className: "lqe-qe__panel-hd-name-7c26fa76" }, step.name));
        }
        const param = ((query === null || query === void 0 ? void 0 : query.params) || []).find(s => s.id === selectedStep);
        if (param) {
            return React.createElement(React.Fragment, null,
                React.createElement("span", { className: "lqe-qe__panel-hd-step-7c26fa76" }, "Param:"),
                React.createElement("div", { className: "lqe-qe__panel-hd-ico-7c26fa76" },
                    React.createElement(Icon, { icon: ['fas', 'sliders-h'] })),
                React.createElement("span", { className: "lqe-qe__panel-hd-name-7c26fa76" }, param.name || 'Parameter'));
        }
        return null;
    }
    return React.createElement("div", { ref: containerRef, className: "lqe-qe-7c26fa76" },
        React.createElement("div", { className: "lqe-qe__panel-hd-7c26fa76" },
            React.createElement("div", { className: "lqe-qe__panel-hd-left-7c26fa76" }, $L('lqe.qe.pipeline')),
            React.createElement("div", { className: "lqe-qe__panel-hd-right-7c26fa76" },
                React.createElement("div", { className: "lqe-qe__panel-hd-title-7c26fa76" }, renderContentHeader()),
                React.createElement("div", { className: "lqe-qe__panel-hd-actions-7c26fa76" },
                    React.createElement("div", { className: classNames("lqe-qe__preview-btn-7c26fa76", {
                            "lqe-qe__preview-btn--active-7c26fa76": showPreview
                        }), onClick: () => setShowPreview(v => !v) },
                        React.createElement("div", { className: "lqe-qe__preview-btn-ico-7c26fa76" },
                            React.createElement(Icon, { icon: showPreview ? "fas eye-slash" : "fas eye" })),
                        React.createElement("span", null, $L('lqe.qe.preview'))),
                    !sourceOnly && React.createElement("div", { className: "lqe-qe__hd-copilot-7c26fa76" },
                        React.createElement(Copilot, { query: query, context: context, onQueryChanged: q => {
                                if (q === null || q === void 0 ? void 0 : q.steps) {
                                    setSelectedStep('');
                                    props.onChange(q);
                                }
                            } }))))),
        React.createElement("div", { className: "lqe-qe__top-7c26fa76" },
            React.createElement("div", { className: "lqe-qe__sidebar-7c26fa76" },
                React.createElement("div", { className: "lqe-qe__sidebar-content-7c26fa76" }, renderPipeline()),
                !sourceOnly && React.createElement("div", { className: "lqe-qe__sidebar-ft-7c26fa76" },
                    React.createElement("div", { className: classNames("lqe-qe__pl-step-7c26fa76", {
                            "lqe-qe__pl-step--selected-7c26fa76": contentMode === 'datasources'
                        }), onClick: () => setContentMode(contentMode === 'datasources' ? 'config' : 'datasources') },
                        React.createElement("div", { className: "lqe-qe__pl-num-7c26fa76" },
                            React.createElement(Icon, { icon: ['fas', 'database'] })),
                        React.createElement("div", { className: "lqe-qe__pl-lbl-7c26fa76" }, $L('lqe.qe.data-sources'))),
                    supportParams && React.createElement("div", { className: classNames("lqe-qe__pl-step-7c26fa76", {
                            "lqe-qe__pl-step--selected-7c26fa76": contentMode === 'params'
                        }), onClick: () => setContentMode(contentMode === 'params' ? 'config' : 'params') },
                        React.createElement("div", { className: "lqe-qe__pl-num-7c26fa76" },
                            React.createElement(Icon, { icon: ['fas', 'sliders-h'] })),
                        React.createElement("div", { className: "lqe-qe__pl-lbl-7c26fa76" }, $L('lqe.qe.parameters'))))),
            React.createElement("div", { className: "lqe-qe__content-7c26fa76" },
                React.createElement("div", { className: "lqe-qe__config-7c26fa76" },
                    contentMode === 'config' && React.createElement(React.Fragment, null,
                        renderInitialSourceMessage(),
                        renderConfiguration()),
                    contentMode === 'datasources' && renderDataSourcesContent(),
                    contentMode === 'params' && renderParamsContent()))),
        showPreview && React.createElement("div", { className: "lqe-qe__drag-7c26fa76", onMouseDown: handlePreviewDragStart }),
        showPreview && React.createElement("div", { className: "lqe-qe__preview-7c26fa76", style: {
                height: previewHeight + 'px'
            } }, renderPreview()),
        React.createElement(DataSourceBrowserModal, { show: showSourceBrowser, onClose: () => setShowSourceBrowser(false), source: null, onSelect: changeSource, showCloseButton: true, origins: [], query: query }));
};
